import { useActiveJobStore } from './activeJobStore';
import { useAuthStore } from './authStore';
import { useCatalogStore } from './catalogStore';
import { useCheckBatchStore } from './checkBatchStore';
import { clearUserScopedStorage } from './clientStorage';
import { useGroupContextStore } from './groupContextStore';
import { useProjectAssetsStore } from './projectAssetsStore';
import { useWalletStore } from './walletStore';

/**
 * clearAllSessionStores — 清空所有「跟登入 user 綁定」的 session store(單一出口)
 * -----------------------------------------------------------------------------
 * 登出時必須清的 7 個 store 收斂成一處,讓**兩條 auth-exit 路徑**清同一組:
 *   1. 手動登出 — `ThemeMenu` 的 `handleLogout`
 *   2. 401-driven `onAuthLost` — `services/index.ts`(refresh chain 已砍,401 直接觸發)
 *
 * **為什麼集中(資安)**:過去手動登出只清 `authStore`,但 `onAuthLost` 清多個。兩條路徑
 * 分歧 → 手動登出後,前一個 user 的 wizard 半成品(brandKitId)/ active job / asset
 * metadata / group context / wallet 點數 / tier-projected catalog 仍殘留在記憶體,下一個
 * user 在同分頁登入就可能讀到 → 跨 user 資料殘留(隱私/安全破口)。集中後兩條路徑等價。
 *
 * **新增 user-scoped store 的維護點**:任何新的「綁定當前 user / group」的 store,clear 邏輯
 * 都在這裡補一行,避免再次分歧。
 *
 * **R226 — 瀏覽器持久層也一起清**(`clearUserScopedStorage`)。這一段原本只是一句
 * 承諾:「store 以外的 user-scoped session 痕跡(billing 的裸 sessionStorage key)同樣
 * 收斂在這裡清」—— 但唯一清過的那把在 R105 隨金流移除刪掉了,之後長出來的三把
 * (`hai.subjectMeta.v1` / `hai.templateDemoAssets.v1` / `ads_discovery`)一把都沒清。
 * 共用電腦換帳號時,下一個人 devtools 打開就看得到上一個人的商品資料與**廣告客戶 id**。
 * 現在鑰匙總表在 [clientStorage](./clientStorage.ts),user/device 二分寫在那裡,
 * 並有機械閘擋「新增 storage 使用點但沒登記」。
 *
 * 注意:Firebase listener detach / `firebaseAuth.signOut()` / BE session revoke 等副作用
 * 由各 caller 自行依其情境順序處理(對齊 ADR-002 onAuthLost / logout 順序)。
 */
export function clearAllSessionStores(): void {
  useAuthStore.getState().clear();
  useActiveJobStore.getState().clearAll();
  // R209 — 批次檢查進度 per-(user,group);換帳號不可殘留他人批次進度與 badge 結果。
  useCheckBatchStore.getState().clearAll();
  useProjectAssetsStore.getState().clearAll();
  useGroupContextStore.getState().clear();
  // wallet 是 per-(user,group) 點數資料(含 cuid / balance);同 gid 換 user 時 cross-group
  // guard 擋不住 stale entry,必須登出即清(R72 二次資安掃描抓到的缺口)。
  useWalletStore.getState().clear();
  // catalog 全域但含 tier 投影(admin 看到較多項);切 user 清掉避免 stale admin catalog 漏給下個 user。
  useCatalogStore.getState().clear();
  // R226 — 最後清瀏覽器持久層的 user-scoped 痕跡(**放在 store 清完之後**:
  // zustand persist 會在 store set 時寫回 localStorage,先 removeItem 會被那次寫回復活)。
  clearUserScopedStorage();
}
